"use client"

import {useCallback, useState, type FormEvent} from "react"
import {Search} from "lucide-react"
import {Input} from "@/components/ui/input"
import {Button} from "@/components/ui/button"
import {getRedirectClick} from "@/lib/click"
import {useAppContext} from "@/contexts/app-context"
import {cleanAmazonUrl, normalizeUrl} from "@/app/amazon/_utils/amazon-url-utils"
import {useTranslations} from "next-intl"
import {cn} from "@/lib/utils"

interface AmazonSearchBoxProps {
  className?: string
  compact?: boolean
}

export function AmazonSearchBox({className, compact}: AmazonSearchBoxProps) {
  const t = useTranslations("start.amazon")
  const {user, clientId, clientVersion} = useAppContext()
  const [term, setTerm] = useState("")

  const performSearch = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      const searchTerm = term.trim()
      if (!searchTerm) return
      const normalized = normalizeUrl(searchTerm)
      const destination = normalized
        ? cleanAmazonUrl(normalized)
        : `https://www.amazon.de/s?k=${encodeURIComponent(searchTerm)}`
      window.open(getRedirectClick(destination, user?.id, clientId, clientVersion), "_blank")
    },
    [term, user?.id, clientId, clientVersion],
  )

  return (
    <form onSubmit={performSearch} className={className}>
      <div className="relative">
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={t("searchPlaceholder")}
          className={cn(
            "rounded-xl bg-white/65",
            compact ? "h-9 pr-10" : "h-12 pr-12",
          )}
        />
        <Button
          type="submit"
          size="icon"
          aria-label={t("searchButton")}
          className={cn(
            "absolute top-1/2 -translate-y-1/2 rounded-lg",
            compact ? "right-1 h-7 w-7" : "right-1.5 h-9 w-9",
          )}
        >
          <Search className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </Button>
      </div>
    </form>
  )
}
